import PortfolioItemContext from '@/components/ChallengePortfolio/NormalView/portfolio-item/PortfolioItemContext';
import { IMAGE_FILE_SIZE_LIMIT } from '@/components/FileAttachments/AddFiles/AddFilesControls/ImageUpload/ImageUploadOption/getFilesToUpload';
import downsizeImage, {
  ImageResizeError,
} from '@/components/FileAttachments/AddFiles/useUploadImages/downsizeImage';
import { useContext, useState } from 'react';
import api from '@/lib/api';
import HttpMethod from '@/lib/constants/HttpMethod.enum';
import ApiError from '@/lib/api/ApiError';
import * as Sentry from '@sentry/react';
import type { UploadedImage } from '@/lib/types';

export enum UploadError {
  FILE_TOO_LARGE,
  GENERIC,
}

const useUploadThumbnail = (): [
  (files: FileList | null) => Promise<UploadedImage | null>,
  boolean,
  UploadError | null,
  () => void,
] => {
  const {
    setPortfolioItem,
    setPortfolioItemSummary,
    portfolioItemApiEndpoint,
  } = useContext(PortfolioItemContext);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<UploadError | null>(null);

  const uploadThumbnail = async (
    files: FileList | null,
  ): Promise<UploadedImage | null> => {
    setUploadError(null);

    if (!files || files.length === 0) {
      return null;
    }

    setUploading(true);

    const imageFile = files[0];

    if (imageFile.size > IMAGE_FILE_SIZE_LIMIT) {
      setUploadError(UploadError.FILE_TOO_LARGE);
      setUploading(false);

      return null;
    }

    try {
      const downsizedImage = await downsizeImage(imageFile);

      const formData = new FormData();
      formData.append('image', downsizedImage);

      const thumbnailResponse = await api.call<UploadedImage>(
        `${portfolioItemApiEndpoint}/thumbnail`,
        {
          method: HttpMethod.POST,
          body: formData,
        },
      );

      setPortfolioItem(portfolioItem => {
        portfolioItem.thumbnail = thumbnailResponse;
      });

      setPortfolioItemSummary(portfolioItemSummary => {
        portfolioItemSummary.thumbnail = thumbnailResponse;
      });

      return thumbnailResponse;
    } catch (error) {
      if (
        !(error instanceof ImageResizeError) &&
        !(error instanceof ApiError)
      ) {
        Sentry.captureException(error);
      }

      setUploadError(UploadError.GENERIC);

      return null;
    } finally {
      setUploading(false);
    }
  };

  const dismissUploadError = (): void => {
    setUploadError(null);
  };

  return [uploadThumbnail, uploading, uploadError, dismissUploadError];
};

export default useUploadThumbnail;
